import React, { useMemo, useState } from "react";
import { Box, Typography, Divider, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import { useVehicleReservation } from "../../common/Providers/VehicleReservationProvider/UseVehicleReservation";
import { useStoreDetails } from "../../common/Providers/StoreDetailsProvider/UseStoreDetails";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import { API_URL } from "../../common/AppConstants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImage } from "@fortawesome/free-regular-svg-icons";
import DiscountCodes from "./DiscountCodes";
import { useManageKeys } from "../../common/Providers/ManageKeysProvider/UseManageKeys";
import Editable from "../../cms/Editable";
import { getTaxesAndFees } from "../../common/pricing";

interface SummaryCardProps {
  isCheckoutStage?: boolean;
  showDiscount?: boolean;
}

const PackageDetails: React.FC<{ item: any }> = ({ item }) => {
  const { VehicleReservationMain } = useVehicleReservation();

  if (!VehicleReservationMain.packageData?.length || !VehicleReservationMain.selectedDuration) {
    return null;
  }

  // Parse selected duration to get duration value and type
  const durationMatch = VehicleReservationMain.selectedDuration.match(/(\d+)-(hour|day|week)/);
  if (!durationMatch) return null;

  const [, duration, type] = durationMatch;
  const durationType = type === 'hour' ? 1 : type === 'day' ? 2 : 3;

  const filteredPackages = VehicleReservationMain.packageData
    .filter((packageItem: any) =>
      packageItem.group_ids &&
      packageItem.group_ids.includes(item.price_group_id) &&
      packageItem.duration_type === durationType &&
      packageItem.duration.toString() === duration
    );

  if (filteredPackages.length === 0) return null;

  return (
    <Box sx={{ mt: 1, ml: 4 }}>
      {filteredPackages.map((packageItem: any, pkgIndex: number) => {
        const groupInclusions =
          packageItem.inclusions?.filter(
            (inclusion: any) => inclusion.group_id === item.price_group_id
          ) || [];

        return (
          <Box key={pkgIndex} sx={{ mb: 1 }}>
            <Typography
              variant="caption"
              sx={{ fontSize: '11px', color: '#666', fontWeight: 500, mb: 0.5 }}
            >
              What's included:
            </Typography>
            {groupInclusions.length > 0 && (
              <Box sx={{ ml: 1 }}>
                <Box
                  component="ul"
                  sx={{
                    pl: 2,
                    m: 0,
                    listStyleType: 'disc',
                    color: '#555',          // darker than #888
                    fontSize: '14px',       // base size for list
                    lineHeight: 1.6,
                  }}
                >
                  {groupInclusions.map((inclusion: any, incIndex: number) => {
                    const items = [];

                    if (inclusion.included_miles > 0) {
                      items.push(`${inclusion.included_miles} miles included`);
                    }

                    if (inclusion.included_battery_percent > 0) {
                      items.push(`${inclusion.included_battery_percent}% battery included`);
                    }

                    if (inclusion.extra_mile_price) {
                      items.push(`Extra miles: $${inclusion.extra_mile_price}`);
                    }

                    const text = items.length > 0 ? items.join(' • ') : 'Standard package';

                    return (
                      <Box
                        key={incIndex}
                        component="li"
                        sx={{
                          fontSize: '14px',   // 👈 increase from 13px
                          lineHeight: 1.6,
                          mb: 0.5,
                        }}
                      >
                        {text}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

const SummaryCard: React.FC<SummaryCardProps> = ({
  isCheckoutStage = false,
  showDiscount = true,
}) => {
  const navigate = useNavigate();
  const { VehicleReservationMain, VehicleReservationItems, removeVehicleReservationItem, clearVehicleReservationItems, setVehicleReservationValue } = useVehicleReservation();
  const { storeDetails, categories } = useStoreDetails();
  const { getKey, getKeyItem, getFlag } = useManageKeys();

  const [imageLoadError, setImageLoadError] = useState(false);
  const [logoLoadError, setLogoLoadError] = useState(false);

  // Handle removing a specific item group from cart
  const handleRemoveItem = (familyId: number) => {
    // Check if this is the last item group before removing
    const currentItemCount = VehicleReservationItems.length;
    const indicesToRemove: number[] = [];
    
    VehicleReservationItems.forEach((item: any, index: number) => {
      if (item.family_id === familyId) {
        indicesToRemove.push(index);
      }
    });
    
    const isLastItemGroup = currentItemCount === indicesToRemove.length;
    
    // If this is the last item, clear everything properly
    if (isLastItemGroup) {
      clearVehicleReservationItems();
      setVehicleReservationValue('pickup', null);
      setVehicleReservationValue('dropoff', null);
      setVehicleReservationValue('pickup_time', null);
      setVehicleReservationValue('dropoff_time', null);
      setVehicleReservationValue('selectedDuration', null);
      setVehicleReservationValue('availableSheet', null);
      navigate('/rental?step=schedule');
    } else {
      // Remove specific items in reverse order to avoid index shifting
      for (let i = indicesToRemove.length - 1; i >= 0; i--) {
        removeVehicleReservationItem(indicesToRemove[i]);
      }
    }
  };

  const selectedCategory = useMemo(() => {
    return categories.find((category: any) => category.id === VehicleReservationMain.mainCategory);
  }, [categories, VehicleReservationMain.mainCategory]);

  // Helper function to format date only
  const formatDate = (date: Date | null) => {
    if (!date) return "";
    const options: Intl.DateTimeFormatOptions = {
      weekday: "long",
      month: "short",
      day: "numeric",
    };
    return date.toLocaleDateString("en-US", options);
  };

  // Helper function to format time only
  const formatTime = (date: Date | null) => {
    if (!date) return "";
    const options: Intl.DateTimeFormatOptions = {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    };
    return date.toLocaleTimeString("en-US", options);
  };

  // Helper function to format time string
  const formatTimeString = (timeString: string | null) => {
    if (!timeString) return "";
    
    // If it's already in a readable format, return as is
    // Otherwise, try to parse and format it
    try {
      const [hours, minutes] = timeString.split(':');
      const hour = parseInt(hours);
      const minute = parseInt(minutes);
      
      if (isNaN(hour) || isNaN(minute)) {
        return timeString; // Return as is if parsing fails
      }
      
      const period = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
      
      return `${displayHour}:${minute.toString().padStart(2, '0')} ${period}`;
    } catch {
      return timeString; // Return as is if formatting fails
    }
  };

  // Calculate rental duration
  const calculateDuration = () => {
    if (!VehicleReservationMain.selectedDuration) return "";
    
    // Parse selected duration to get duration value and type
    const durationMatch = VehicleReservationMain.selectedDuration.match(/(\d+)-(hour|day|week)/);
    if (!durationMatch) return "";
    
    const [, duration, type] = durationMatch;
    const durationNum = parseInt(duration, 10);
    const formattedType = type.charAt(0).toUpperCase() + type.slice(1);
    
    return `${duration} ${formattedType}${durationNum > 1 ? "s" : ""}`;
  };

  // Get category name for vehicle display
  // const getCategoryName = () => {
  //   const name =
  //     selectedCategory?.category || selectedCategory?.name || "Vehicle";
  //   return name + ` || ${storeDetails.store_name}`;
  // };

  // Group items by display_name and sum quantities
  const getGroupedItems = () => {
    if (!VehicleReservationItems || VehicleReservationItems.length === 0) return [];

    const grouped = VehicleReservationItems.reduce((acc, item) => {
      const key = item.display_name || item.family;
      if (acc[key]) {
        acc[key].quantity += item.quantity || 0;
      } else {
        acc[key] = {
          ...item,
          quantity: item.quantity || 0,
        };
      }
      return acc;
    }, {} as Record<string, any>);

    return Object.values(grouped);
  };

  // Get customer information from localStorage or reservation data
  const getCustomerInfo = () => {
    return {
      name: VehicleReservationMain.name || "Customer",
      email: VehicleReservationMain.email || "",
      phone: VehicleReservationMain.phone_number || "",
    };
  };

  // Format price for display
  const formatPrice = (price: number) => {
    return `$${price.toFixed(2)}`;
  };

  const subtotal = VehicleReservationMain.prices?.subtotal || 0.0;
  const discountAmount = VehicleReservationMain.prices?.discount || 0.0;
  const serviceFee = VehicleReservationMain.prices?.service_fee || 0.0;
  // CMS checkbox: on = one combined "Taxes & Fees" line, off (default) = the
  // itemized Bodhi Protect / Service Fee / tax breakdown.
  const combineTaxesAndFees = getFlag('combine_taxes_and_fees');
  const taxesAndFees = getTaxesAndFees(
    VehicleReservationMain.prices,
    VehicleReservationMain
  );

  if (isCheckoutStage) {
    const customerInfo = getCustomerInfo();

    return (
      <Box sx={styles.summaryContainer}>
        <Box sx={styles.summaryContent}>
          <Box sx={styles.checkoutHeader}>
            <Typography sx={styles.checkoutHeaderTitle}>
              Your Order
            </Typography>
          </Box>

          <Divider sx={styles.sectionDivider} />

          <Box sx={styles.checkoutContent}>
            <Box sx={styles.customerSection}>
              <Typography sx={styles.customerName}>
                {customerInfo.name}
              </Typography>

              {customerInfo.email && (
                <Box sx={styles.contactRow}>
                  <EmailIcon sx={styles.contactIcon} />
                  <Typography sx={styles.contactText}>
                    {customerInfo.email}
                  </Typography>
                </Box>
              )}

              {customerInfo.phone && (
                <Box sx={styles.contactRow}>
                  <PhoneIcon sx={styles.contactIcon} />
                  <Typography sx={styles.contactText}>
                    {customerInfo.phone}
                  </Typography>
                </Box>
              )}
            </Box>

            <Divider sx={styles.sectionDivider} />

            <Box sx={styles.pricingSection}>
              <Box sx={styles.priceRow}>
                <Typography sx={styles.priceLabel}>
                  Tickets
                </Typography>
                <Typography sx={styles.priceValue}>
                  {formatPrice(subtotal)}
                </Typography>
              </Box>

              {discountAmount > 0 && (
                <Box sx={styles.priceRow}>
                  <Typography sx={styles.priceLabel}>
                    Discount
                  </Typography>
                  <Typography
                    sx={{
                      ...styles.priceValue,
                      color: "#e53935",
                    }}
                  >
                    {discountAmount > 0
                      ? `-${formatPrice(discountAmount)}
  `
                      : formatPrice(0)}
                  </Typography>
                </Box>
              )}

              {!combineTaxesAndFees && VehicleReservationMain.bodhi_protect_price > 0 && (
                <Box sx={styles.priceRow}>
                  <Typography sx={styles.priceLabel}>
                    Bodhiprotect
                  </Typography>
                  <Typography sx={styles.priceValue}>
                    {formatPrice(VehicleReservationMain.bodhi_protect_price || 0.0)}
                  </Typography>
                </Box>
              )}

              {VehicleReservationMain.delivery_charge > 0 && VehicleReservationMain.orderType === 'delivery' && (
                <Box sx={styles.priceRow}>
                  <Typography sx={styles.priceLabel}>
                    Delivery Charge
                  </Typography>
                  <Typography sx={styles.priceValue}>
                    {formatPrice(VehicleReservationMain.delivery_charge || 0.0)}
                  </Typography>
                </Box>
              )}

              {/* {VehicleReservationMain.upcharge && VehicleReservationMain.upcharge > 0 && (
                <Box sx={styles.priceRow}>
                  <Typography sx={styles.priceLabel}>
                    Extra Charge
                  </Typography>
                  <Typography sx={styles.priceValue}>
                    {formatPrice(VehicleReservationMain.upcharge)}
                  </Typography>
                </Box>
              )} */}

              {!combineTaxesAndFees && storeDetails?.is_service_fee_enabled && serviceFee > 0 && (
                <Box sx={styles.priceRow}>
                  <Typography sx={styles.priceLabel}>
                    Service Fee
                  </Typography>
                  <Typography sx={styles.priceValue}>
                    {formatPrice(serviceFee)}
                  </Typography>
                </Box>
              )}

              <Box sx={styles.priceRow}>
                {combineTaxesAndFees ? (
                  <Editable
                    keyName="taxes_and_fees_label"
                    fieldType="text"
                    label="Taxes & Fees Label"
                    value={getKey('taxes_and_fees_label', 'Taxes & Fees')}
                    display="inline-block"
                  >
                    <Typography sx={styles.priceLabel}>
                      {getKey('taxes_and_fees_label', 'Taxes & Fees')}
                    </Typography>
                  </Editable>
                ) : (
                  <Typography sx={styles.priceLabel}>
                    {`Tax (${storeDetails?.sales_tax ? (storeDetails.sales_tax / 100).toLocaleString(undefined, { style: 'percent' }) : 'n/a'})`}
                  </Typography>
                )}
                <Editable
                  keyName="combine_taxes_and_fees"
                  fieldType="checkbox"
                  label="Combine Taxes & Fees"
                  value={combineTaxesAndFees ? '1' : '0'}
                  display="inline-block"
                >
                  <Typography sx={styles.priceValue}>
                    {formatPrice(combineTaxesAndFees ? taxesAndFees : (VehicleReservationMain.prices?.tax || 0.0))}
                  </Typography>
                </Editable>
              </Box>

              {showDiscount && (
                <Box sx={styles.discountSection}>
                  <Typography sx={styles.discountLabel}>
                    Discount Code
                  </Typography>
                  <DiscountCodes />
                </Box>
              )}

              <Divider sx={styles.totalDivider} />

              <Box sx={styles.totalRow}>
                <Typography sx={styles.totalLabel}>
                  Total
                </Typography>
                <Typography sx={styles.totalValue}>
                  {formatPrice(VehicleReservationMain.prices?.total || 0.0)}
                </Typography>
              </Box>
            </Box>

            <Divider sx={styles.sectionDivider} />

            {(VehicleReservationMain.pickup ||
              VehicleReservationMain.dropoff ||
              VehicleReservationItems?.length > 0) && (
              <Box sx={styles.rentalSection}>
                {/* <Typography sx={styles.deliveryTitle}>
                  {getCategoryName()}
                </Typography> */}

                <Box sx={styles.deliveryDetails}>
                  {VehicleReservationItems?.length > 0 && (
                    <Box sx={styles.itemsContainer}>
                      {getGroupedItems().map((item: any, index: number) => (
                        <Box key={index}>
                          <Box sx={{ ...styles.vehicleRow, position: 'relative', paddingRight: !isCheckoutStage ? '32px' : 0 }}>
                            <DirectionsCarIcon sx={styles.icon} />
                            <Typography sx={{ ...styles.vehicleText, flex: 1, paddingRight: '8px' }}>
                              {item.quantity}x {item.display_name || item.family}
                            </Typography>
                            {!isCheckoutStage && (
                              <IconButton
                                onClick={() => handleRemoveItem(item.family_id)}
                                sx={{
                                  position: 'absolute',
                                  right: 0,
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  padding: '4px',
                                  '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                  },
                                }}
                                size="small"
                              >
                                <CloseIcon sx={{ fontSize: '16px', color: '#666' }} />
                              </IconButton>
                            )}
                          </Box>
                          <PackageDetails item={item} />
                        </Box>
                      ))}
                    </Box>
                  )}

                  {(VehicleReservationMain.pickup || VehicleReservationMain.dropoff) && (
                    <Box sx={styles.dateTimeRow}>
                      <CalendarTodayIcon sx={styles.icon} />
                      <Box>
                        {VehicleReservationMain.pickup && (
                          <>
                            <Typography sx={styles.dateText}>
                              {formatDate(VehicleReservationMain.pickup)}{VehicleReservationMain.pickup_time ? ` at ${formatTimeString(VehicleReservationMain.pickup_time)}` : ''}
                            </Typography>
                          </>
                        )}
                        {VehicleReservationMain.dropoff && (
                          <Typography sx={styles.dateSubtext}>
                            Until {formatDate(VehicleReservationMain.dropoff)} at {formatTimeString(VehicleReservationMain.dropoff_time)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}

                  {VehicleReservationMain.pickup && VehicleReservationMain.dropoff && (
                    <Box sx={styles.dateTimeRow}>
                      <AccessTimeIcon sx={styles.icon} />
                      <Box>
                        <Typography sx={styles.dateText}>
                          Rental Duration
                        </Typography>
                        <Typography sx={styles.dateSubtext}>
                          {calculateDuration()}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={styles.summaryContainer}>
      <Box sx={styles.summaryContent}>
        <Box sx={styles.vehicleImageContainer}>
          {(() => {
            const img01 = getKeyItem('right_panel_image_01');
            const src = img01?.image_url ? `${API_URL}${img01.image_url}` : API_URL + selectedCategory?.img_url;
            const altText = img01?.value || selectedCategory?.category || 'Vehicle';
            return (
              <Editable keyName="right_panel_image_01" fieldType="image" label="Right Panel Image 1" value={img01?.image_url || ''}>
                <img
                  src={src}
                  alt={altText}
                  title={altText}
                  style={{
                    width: "100%",
                    height: "auto",
                    maxHeight: "300px",
                    backgroundColor: "white",
                    objectFit: "contain",
                    display: imageLoadError ? "none" : "block",
                  }}
                  onError={() => setImageLoadError(true)}
                  onLoad={() => setImageLoadError(false)}
                />
                {imageLoadError && (
                  <FontAwesomeIcon
                    icon={faImage}
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      width: "60px",
                      height: "60px",
                      color: "#666",
                    }}
                  />
                )}
              </Editable>
            );
          })()}
        </Box>

        <Box sx={styles.rideOffSection}>
          {(() => {
            const item = getKeyItem('right_panel_text');
            const fallback = `Get your ${selectedCategory?.category} to directly to your off-island location!`;
            return (
              <Editable keyName="right_panel_text" fieldType="html" label="Right Panel Text" value={item?.value || ''}>
                {item?.content_type === 'html' ? (
                  <div className="ql-editor" style={{ padding: 0 }} dangerouslySetInnerHTML={{ __html: item.value }} />
                ) : (
                  <Typography sx={styles.rideOffTitle}>{getKey('right_panel_text', fallback)}</Typography>
                )}
              </Editable>
            );
          })()}
          {/* <Typography sx={styles.rideOffDescription}>
            Experience the open air while shopping, visiting friends or just
            doing vacation life. Maybe you have a wedding, special party or
            social event and want to arrive in style. Whatever the
          </Typography> */}
        </Box>

        {/* Delivery Details Section */}
        {(VehicleReservationMain.pickup ||
          VehicleReservationMain.dropoff ||
          VehicleReservationItems?.length > 0) && (
          <Box sx={styles.deliverySection}>
            {/* <Typography sx={styles.deliveryTitle}>
              {getCategoryName()}
            </Typography> */}

            <Box sx={styles.deliveryDetails}>
              {VehicleReservationItems?.length > 0 && (
                <Box sx={styles.itemsContainer}>
                  {getGroupedItems().map((item: any, index: number) => (
                     <Box key={index}>
                        <Box sx={{ ...styles.vehicleRow, position: 'relative', paddingRight: !isCheckoutStage ? '32px' : 0 }}>
                          <DirectionsCarIcon sx={styles.icon} />
                          <Typography sx={{ ...styles.vehicleText, flex: 1, paddingRight: '8px' }}>
                            {item.quantity}x {item.display_name || item.family}
                          </Typography>
                          {!isCheckoutStage && (
                            <IconButton
                              onClick={() => handleRemoveItem(item.family_id)}
                              sx={{
                                position: 'absolute',
                                right: 0,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                padding: '4px',
                                '&:hover': {
                                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                },
                              }}
                              size="small"
                            >
                              <CloseIcon sx={{ fontSize: '16px', color: '#666' }} />
                            </IconButton>
                          )}
                        </Box>
                        <PackageDetails item={item} />
                      </Box>
                  ))}
                </Box>
              )}

              <Divider sx={{ my: 1 }} />

              {(VehicleReservationMain.pickup || VehicleReservationMain.dropoff) && (
                <Box sx={styles.dateTimeRow}>
                  <CalendarTodayIcon sx={styles.icon} />
                  <Box>
                    {VehicleReservationMain.pickup && (
                      <>
                        <Typography sx={styles.dateText}>
                          {formatDate(VehicleReservationMain.pickup)}{VehicleReservationMain.pickup_time ? ` at ${formatTimeString(VehicleReservationMain.pickup_time)}` : ''}
                        </Typography>
                      </>
                    )}
                    {VehicleReservationMain.dropoff && (
                      <Typography sx={styles.dateSubtext}>
                        Until {formatDate(VehicleReservationMain.dropoff)} at {formatTimeString(VehicleReservationMain.dropoff_time)}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}

              {VehicleReservationMain.pickup && VehicleReservationMain.dropoff && (
                <Box sx={styles.dateTimeRow}>
                  <AccessTimeIcon sx={styles.icon} />
                  <Box>
                    <Typography sx={styles.dateText}>
                      Rental Duration
                    </Typography>
                    <Typography sx={styles.dateSubtext}>
                      {calculateDuration()}
                    </Typography>
                  </Box>
                </Box>
              )}

            </Box>
          </Box>
        )}

        {/* Carolina Rides Branding */}
        <Box sx={styles.brandingSection}>
          {(() => {
            const img02 = getKeyItem('right_panel_image_02');
            const src = img02?.image_url ? `${API_URL}${img02.image_url}` : API_URL + storeDetails?.logo_url;
            const altText = img02?.value || storeDetails?.store_name;
            return (
              <Editable keyName="right_panel_image_02" fieldType="image" label="Right Panel Image 2" value={img02?.image_url || ''}>
                <img
                  src={src}
                  alt={altText}
                  title={altText}
                  style={{ width: '100%', height: 'auto', maxHeight: '300px', objectFit: 'contain', display: logoLoadError ? 'none' : 'block' }}
                  onError={() => setLogoLoadError(true)}
                  onLoad={() => setLogoLoadError(false)}
                />
                {logoLoadError && <FontAwesomeIcon icon={faImage} style={{height: '75px', color: "#333"}} />}
              </Editable>
            );
          })()}
        </Box>
      </Box>
    </Box>
  );
};

const styles = {
  summaryContainer: {
    width: "100%",
    margin: {
      xs: "12px 0px 0px 0px",
      md: "0px 0px 0px 12px",
    },
    borderRadius: "4px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    overflow: "hidden",
    height: "auto",
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#e6edf9",
    paddingBottom: {
      xs: "64px",
      md: "0px",
    },
  },
  checkoutHeader: {
    padding: "16px 16px 0 16px",
    borderRadius: "16px 16px 0 0",
  },
  checkoutHeaderTitle: {
    fontSize: "20px",
    fontWeight: 600,
    color: "#333333",
    margin: 0,
    letterSpacing: "-0.025em",
  },
  checkoutContent: {
    padding: "0px 16px",
  },
  customerSection: {
    marginBottom: "8px",
  },
  customerName: {
    fontSize: "16px",
    fontWeight: 700,
    color: "#1f2937",
    marginBottom: "12px",
    letterSpacing: "-0.025em",
  },
  contactRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "12px",
  },
  contactIcon: {
    fontSize: "18px",
    color: "#6b7280",
  },
  contactText: {
    fontSize: "15px",
    color: "#6b7280",
    fontWeight: 400,
  },
  sectionDivider: {
    margin: "8px 0",
    borderColor: "#ccc",
    borderWidth: "1px",
  },
  pricingSection: {
    marginBottom: "8px",
  },
  totalDivider: {
    margin: "16px 0",
    borderColor: "#d1d5db",
    borderWidth: "1px",
  },
  rentalSection: {
    marginBottom: "8px",
  },
  rentalTitle: {
    fontSize: "18px",
    fontWeight: 700,
    color: "#1f2937",
    marginBottom: "20px",
    letterSpacing: "-0.025em",
  },
  dateSection: {
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
    marginBottom: "16px",
  },
  dateIcon: {
    fontSize: "20px",
    color: "#6b7280",
    marginTop: "2px",
  },
  vehicleSection: {
    marginTop: "16px",
  },
  vehicleIcon: {
    fontSize: "20px",
    color: "#6b7280",
  },
  vehiclePrice: {
    fontSize: "16px",
    fontWeight: 700,
    color: "#1f2937",
    marginLeft: "auto",
  },
  changeInfoSection: {
    marginTop: "20px",
    padding: "16px 0px",
    borderTop: "1px dotted #d1d5db",
  },
  changeInfoLink: {
    fontSize: "15px",
    color: "#2563eb",
    cursor: "pointer",
    textDecoration: "none",
    fontWeight: 500,
    "&:hover": {
      textDecoration: "underline",
      color: "#1d4ed8",
    },
  },
  summaryHeader: {
    backgroundColor: "#0047C0",
    color: "white",
    padding: "16px",
    textAlign: "center",
  },
  summaryTitle: {
    fontWeight: 600,
    color: "white",
    fontSize: "18px",
    margin: 0,
  },
  summaryContent: {
    overflowY: "auto",
    flex: 1,
  },
  vehicleImageContainer: {
    position: "relative",
    marginBottom: "8px",
    borderRadius: "4px 4px 0 0",
    overflow: "hidden",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    backgroundColor: "#f5f5f5",
  },
  rideOffSection: {
    marginBottom: "20px",
    padding: "16px",
  },
  rideOffTitle: {
    fontSize: "14px",
    color: "#333",
    marginBottom: "12px",
    fontWeight: 400,
    textAlign: "left",
    lineHeight: 1.4,
  },
  rideOffDescription: {
    fontSize: "14px",
    color: "#666",
    lineHeight: 1.4,
    textAlign: "left",
    marginBottom: "8px",
  },
  showMoreLink: {
    fontSize: "14px",
    color: "#0066cc",
    cursor: "pointer",
    textDecoration: "underline",
  },
  deliverySection: {
    marginBottom: "20px",
    padding: "16px",
    backgroundColor: "#fff",
    borderRadius: "8px",
    border: "1px solid #e0e0e0",
  },
  deliveryTitle: {
    fontSize: "16px",
    fontWeight: 600,
    color: "#333",
    marginBottom: "16px",
  },
  deliveryDetails: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  dateTimeRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
  },
  icon: {
    fontSize: "18px",
    color: "#666",
    marginTop: "2px",
  },
  dateText: {
    fontSize: "15px",
    color: "#1f2937",
    fontWeight: 600,
    lineHeight: 1.4,
  },
  dateSubtext: {
    fontSize: "15px",
    color: "#6b7280",
    lineHeight: 1.4,
    fontWeight: 400,
  },
  durationText: {
    fontSize: "15px",
    color: "#1f2937",
    fontWeight: 600,
    marginLeft: "32px",
  },
  itemsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  vehicleRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "8px",
  },
  vehicleText: {
    fontSize: "15px",
    color: "#1f2937",
    fontWeight: 600,
  },
  categoryDetails: {
    marginBottom: "20px",
    padding: "16px",
    backgroundColor: "#f0f8ff",
    borderRadius: "8px",
    borderLeft: "4px solid #0047C0",
  },
  categoryTitle: {
    fontSize: "16px",
    fontWeight: 600,
    color: "#0047C0",
    marginBottom: "8px",
  },
  categoryDescription: {
    fontSize: "14px",
    color: "#555",
    lineHeight: 1.4,
  },
  rentalDetails: {
    marginBottom: "20px",
    padding: "16px",
    backgroundColor: "#fff",
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
  },
  detailsTitle: {
    fontSize: "16px",
    fontWeight: 600,
    color: "#333",
    marginBottom: "12px",
  },
  detailRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "8px",
  },
  detailLabel: {
    fontSize: "14px",
    color: "#666",
    fontWeight: 500,
  },
  detailValue: {
    fontSize: "14px",
    color: "#333",
    fontWeight: 600,
  },
  pricingSummary: {
    marginBottom: "20px",
    padding: "16px",
    backgroundColor: "#fff",
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
  },
  pricingTitle: {
    fontSize: "16px",
    fontWeight: 600,
    color: "#333",
    marginBottom: "12px",
  },
  priceRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "12px",
  },
  priceLabel: {
    fontSize: "16px",
    color: "#6b7280",
    fontWeight: 400,
  },
  priceValue: {
    fontSize: "16px",
    color: "#1f2937",
    fontWeight: 600,
  },
  discountValue: {
    fontSize: "16px",
    color: "#4CAF50",
    fontWeight: 600,
  },
  discountSection: {
    marginTop: "16px",
    marginBottom: "8px",
  },
  discountLabel: {
    fontSize: "14px",
    fontWeight: 500,
    color: "#666",
    marginBottom: "8px",
  },
  totalRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "16px",
  },
  totalLabel: {
    fontSize: "18px",
    color: "#1f2937",
    fontWeight: 700,
    letterSpacing: "-0.025em",
  },
  totalValue: {
    fontSize: "24px",
    color: "#2563eb",
    fontWeight: 800,
    letterSpacing: "-0.025em",
  },
  brandingSection: {
    textAlign: "center",
    padding: "0",
    marginTop: "auto",
    overflow: "hidden",
  },
  brandName: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#0047C0",
    marginBottom: "4px",
  },
  brandSubtext: {
    fontSize: "16px",
    fontWeight: 600,
    color: "#00BFFF",
    letterSpacing: "2px",
  },
};

export default SummaryCard;
